import { useEffect, useMemo, useState } from "react";
import API, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Checkbox } from "../ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import { toast } from "sonner";
import { ShieldCheck, Flask, Books, Archive } from "@phosphor-icons/react";

const TABS = [
  { key: "rams", label: "RAMS", icon: ShieldCheck },
  { key: "coshh", label: "COSHH", icon: Flask },
  { key: "hse_docs", label: "HSE documents", icon: Books },
  { key: "compliance", label: "Compliance Hub", icon: Archive },
];

/** Rows the picker offers per tab — each row is one addable {kind, ref_id}. */
function rowsFor(tab, lib, q) {
  const s = q.trim().toLowerCase();
  const hit = (...parts) => !s || parts.filter(Boolean).join(" ").toLowerCase().includes(s);
  if (tab === "rams") return (lib.rams || []).filter(r => hit(r.reference, r.title, r.client_name, r.site_address)).map(r => ({ kind: "rams", ref_id: r.id, title: r.title || r.reference, sub: `${r.reference} · v${r.version || 1} · ${r.status}${r.client_name ? ` · ${r.client_name}` : ""}` }));
  if (tab === "hse_docs") return (lib.hse_docs || []).filter(d => hit(d.reference, d.title, d.type_label)).map(d => ({ kind: "hse_doc", ref_id: d.id, title: d.title || d.reference, sub: `${d.reference} · ${d.type_label || ""} · v${d.version || 1} · ${d.status}` }));
  if (tab === "compliance") return (lib.compliance || []).filter(d => hit(d.title, d.category)).map(d => ({ kind: "compliance", ref_id: d.id, title: d.title, sub: `${(d.category || "").replace(/_/g, " ")} · v${d.current_version_number || 1}` }));
  const out = [];
  (lib.coshh || []).filter(c => hit(c.reference, c.product, c.manufacturer)).forEach(c => {
    if (c.assessment_ready) out.push({ kind: "coshh_assessment", ref_id: c.id, title: `${c.product} — COSHH assessment`, sub: `${c.reference} · ${c.manufacturer || ""}${c.signal_word ? ` · ${c.signal_word}` : ""}` });
    if (c.data_sheet_ready) out.push({ kind: "coshh_data_sheet", ref_id: c.id, title: `${c.product} — Product data sheet`, sub: `${c.reference} · ${c.manufacturer || ""}` });
  });
  return out;
}

export default function HsPackPickerDialog({ open, onOpenChange, contractId, existing = [], onAdded }) {
  const [lib, setLib] = useState(null);
  const [tab, setTab] = useState("rams");
  const [q, setQ] = useState("");
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSel({}); setQ("");
    API.get("/contracts/hs-library").then(({ data }) => setLib(data)).catch(() => toast.error("Could not load the document library"));
  }, [open]);

  const have = useMemo(() => new Set(existing.map(i => `${i.kind}:${i.ref_id}`)), [existing]);
  const rows = useMemo(() => (lib ? rowsFor(tab, lib, q) : []), [tab, lib, q]);
  const key = (r) => `${r.kind}:${r.ref_id}`;
  const count = Object.values(sel).filter(Boolean).length;

  const add = async () => {
    const items = Object.entries(sel).filter(([, v]) => v).map(([k]) => { const [kind, ref_id] = k.split(":"); return { kind, ref_id }; });
    if (!items.length) return;
    setBusy(true);
    try {
      const { data } = await API.post(`/contracts/${contractId}/hs-pack`, { items });
      toast.success(`${data.added} document${data.added === 1 ? "" : "s"} added to the H&S pack`);
      onAdded?.(data.items);
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not add documents"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="hs-pack-picker">
        <DialogHeader>
          <DialogTitle className="font-[Chivo]">Add health &amp; safety documents</DialogTitle>
          <DialogDescription>Pick from your RAMS, COSHH register, HSE documents and Compliance Hub. The assigned operatives and the client can then open them from this contract.</DialogDescription>
        </DialogHeader>
        <Tabs value={tab} onValueChange={(v) => { setTab(v); setQ(""); }}>
          <TabsList className="flex-wrap h-auto">
            {TABS.map(t => <TabsTrigger key={t.key} value={t.key} data-testid={`hs-pack-picker-tab-${t.key}`}><t.icon size={13} className="mr-1" /> {t.label}</TabsTrigger>)}
          </TabsList>
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search…" className="h-9 mt-3" data-testid="hs-pack-picker-search" />
          {TABS.map(t => (
            <TabsContent key={t.key} value={t.key} className="mt-3">
              {!lib ? <p className="text-sm text-muted-foreground">Loading…</p> : rows.length === 0 ? <p className="text-sm text-muted-foreground py-6 text-center" data-testid="hs-pack-picker-empty">Nothing here{q ? " for that search" : ""}.</p> : (
                <ul className="divide-y divide-border border border-border rounded-lg max-h-[50vh] overflow-auto">
                  {rows.map(r => {
                    const k = key(r); const already = have.has(k);
                    return (
                      <li key={k} className={`flex items-center gap-3 px-3 py-2 ${already ? "opacity-50" : "hover:bg-muted/40"}`} data-testid={`hs-pack-picker-row-${r.kind}-${r.ref_id}`}>
                        <Checkbox checked={already || !!sel[k]} disabled={already} onCheckedChange={(v) => setSel(s => ({ ...s, [k]: !!v }))} data-testid={`hs-pack-picker-check-${r.kind}-${r.ref_id}`} />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium truncate">{r.title}</p>
                          <p className="text-[11px] text-muted-foreground truncate">{r.sub}{already ? " · already in pack" : ""}</p>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </TabsContent>
          ))}
        </Tabs>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={add} disabled={busy || !count} data-testid="hs-pack-picker-add">{busy ? "Adding…" : `Add ${count || ""}`}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
